import {inject, Injectable} from "@angular/core";
import {HttpClient, HttpParams} from "@angular/common/http";
import {map, Observable, shareReplay} from "rxjs";
import {ApiRoutesService} from '../api-routes.service';
import {Translation} from '../../models/translation/translation.model';
import {Page} from '../../models/pagination/page.model';
import {LanguageService} from './language.service';
import {createPaginationParams} from '../../utils/pagination.mapper';
import {PageSearch} from '../../models/pagination/page-search.model';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {

  readonly #httpClient = inject(HttpClient);
  readonly #routes = inject(ApiRoutesService);
  readonly #languageService = inject(LanguageService);
  #translationCache$: Observable<Map<string, Translation>> | null = null;

  // Repli localStorage des traductions du quiz : les pipes `gameTranslate` sont
  // purs, donc un reload (ou une relance hors `game-loading`, ou un
  // game-loading offline) qui laisse #totemTranslations vide afficherait les
  // clés brutes ET casserait la validation des réponses. On réhydrate au 1er accès.
  readonly #GAME_TRANSLATIONS_STORAGE_KEY = 'wrz.game.translations';

  #translations: Map<string, Translation> | null = null;
  #totemTranslations: Map<string, Translation> | null = null;
  #gameHydrationAttempted = false;

  public findAllTranslations(translateSearch: TranslationSearch, pageSearch?: PageSearch): Observable<Page<Translation>> {
    let params;
    if (pageSearch != null) {
      params = createPaginationParams(pageSearch);
    } else {
        params = new HttpParams();
    }

    if (translateSearch?.lang) {
      params = params!.set('lang', translateSearch.lang);
    }

    if (translateSearch?.code) {
      params = params!.set('code', translateSearch.code);
    }

    if (translateSearch?.status && translateSearch.status !== 'all') {
      params = params!.set('status', translateSearch.status);
    }

    return this.#httpClient.get<Page<Translation>>(this.#routes.translations(), {params});
  }

  // Nombre de clés non traduites d'une langue : le total d'une page d'UNE
  // ligne filtrée côté serveur (pas d'endpoint de comptage dédié).
  countUntranslated(lang: string): Observable<number> {
    return this.findAllTranslations(
      {lang, status: 'untranslated'},
      {page: 0, pageSize: 1, sort: 'key', direction: 'asc'}
    ).pipe(map(page => page.totalElements));
  }

  findAllTranslationsByCode(code: string): Observable<Translation[]> {
    const params = new HttpParams().set('code', code);

    return this.#httpClient.get<Translation[]>(this.#routes.translations(), { params }).pipe(
      map((page: any) => page.content || page)
    );
  }

  getAllTranslations(): Observable<Map<string, Translation>> {
    if (this.#translationCache$ == null) {
      this.#translationCache$ = this.#httpClient.get<Translation[]>(this.#routes.publicTranslations(this.#languageService.getPreferredLanguageCode())).pipe(
        map((translations) => {
          const translationMap = new Map<string, Translation>();
          translations.forEach(translation => {
            translationMap.set(translation.key, translation);
          });

          this.#translations = translationMap;

          return translationMap;
        }),
        shareReplay(1)
      );
    }

    return this.#translationCache$;
  }

  getTotemTranslations(totemId: string): Observable<Map<string, Translation>> {
    return this.#httpClient.get<Translation[]>(this.#routes.publicTranslations(this.#languageService.getPreferredLanguageCode(), totemId)).pipe(
      map((translations) => {
        const translationMap = new Map<string, Translation>();
        translations.forEach(translation => {
          translationMap.set(translation.key, translation);
        });

        this.#totemTranslations = translationMap;
        this.persistGameTranslations(translationMap);

        return translationMap;
      })
    );
  }

  // Traductions du quiz pour un event sans totem (projection) : le thème vient
  // du scénario, résolu côté backend par eventId. Alimente le même cache
  // #totemTranslations (utilisé par gameTranslate) que le flow totem.
  getEventTranslations(eventId: string): Observable<Map<string, Translation>> {
    return this.#httpClient.get<Translation[]>(this.#routes.publicTranslations(this.#languageService.getPreferredLanguageCode(), undefined, eventId)).pipe(
      map((translations) => {
        const translationMap = new Map<string, Translation>();
        translations.forEach(translation => {
          translationMap.set(translation.key, translation);
        });

        this.#totemTranslations = translationMap;
        this.persistGameTranslations(translationMap);

        return translationMap;
      })
    );
  }

  findTranslation(translationId: string): Observable<Translation> {
    return this.#httpClient.get<Translation>(this.#routes.translation(translationId));
  }

  updateTranslation(id: string, key: string, value: string): Observable<Translation> {
    return this.#httpClient.put<Translation>(this.#routes.translation(id), {
      key: key,
      value: value
    });
  }

  createTranslation(key: string, value: string, languageId: string) {
    return this.#httpClient.post<Translation>(this.#routes.translations(), {
      key, value, languageId
    });
  }

  updateTranslations(translations: Translation[]): Observable<Translation[]> {
    return this.#httpClient.put<Translation[]>(this.#routes.translations(), translations);
  }

  initializeTranslations(): Observable<Map<string, Translation>> {
    return this.getAllTranslations();
  }

  resetTranslationCache(): void {
    this.#translationCache$ = null;

    this.getAllTranslations().subscribe();
  }

  resetTranslationCacheAsync(): Observable<Map<string, Translation>> {
    this.#translationCache$ = null;

    return this.getAllTranslations();
  }

  getTranslation(key: string) {
    return this.#translations?.get(key)?.value ?? key;
  }

  getGameTranslation(key: string) {
    if (this.#totemTranslations == null && !this.#gameHydrationAttempted) {
      this.hydrateGameTranslationsFromStorage();
    }
    return this.#totemTranslations?.get(key)?.value ?? key;
  }

  private persistGameTranslations(translations: Map<string, Translation>): void {
    try {
      const payload = {
        lang: this.#languageService.getPreferredLanguageCode(),
        entries: Array.from(translations.values()),
      };
      localStorage.setItem(this.#GAME_TRANSLATIONS_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Quota plein ou storage indisponible (navigation privée stricte) : le
      // cache mémoire suffit pour la session en cours.
    }
  }

  // localStorage est une frontière non fiable : tout JSON.parse peut throw sur
  // une valeur héritée/corrompue. On isole, on purge si illisible, et on ignore
  // un cache d'une autre langue (game-loading rechargera la bonne).
  private hydrateGameTranslationsFromStorage(): void {
    this.#gameHydrationAttempted = true;
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(this.#GAME_TRANSLATIONS_STORAGE_KEY);
    } catch {
      return;
    }
    if (!raw) {
      return;
    }
    try {
      const payload = JSON.parse(raw) as { lang?: string; entries?: Translation[] };
      if (!payload?.entries || payload.lang !== this.#languageService.getPreferredLanguageCode()) {
        return;
      }
      const translationMap = new Map<string, Translation>();
      payload.entries.forEach(translation => {
        translationMap.set(translation.key, translation);
      });
      this.#totemTranslations = translationMap;
    } catch {
      try {
        localStorage.removeItem(this.#GAME_TRANSLATIONS_STORAGE_KEY);
      } catch {
        // purge best-effort
      }
    }
  }

}

// Filtre « traduite / non traduite » appliqué par le back (paramètre `status`),
// sans effet sur la langue par défaut.
export type TranslationStatusFilter = 'all' | 'untranslated' | 'translated';

export interface TranslationSearch {
  lang?: string;
  code?: string;
  status?: TranslationStatusFilter;
}
